import { Box, Tooltip } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import { Flex, IconV3 } from "@rpi/openapi-core";
import React from "react";
import { InfoCircle } from "tabler-icons-react";
import { isNumber } from "../../utils/type.util";
import { useRpiTableSortBy } from "./modules/RpiTableSortBy";
import { useRpiTableSubRow } from "./modules/RpiTableSubRow";
import { RpiTableColumn } from "./RpiTable";
import { justifyFromAlign } from "./RpiTableHelpers";

interface RpiTableHeadProps<T> {
  columns: RpiTableColumn<T>[];
  totalSize: number;
}

export function RpiTableHead<T>({ columns, totalSize }: RpiTableHeadProps<T>): React.ReactElement {
  const withSubRow = useRpiTableSubRow<T>();
  return (
    <thead>
      <tr>
        {columns.map((column, i) => (
          <RpiTableTh key={`rpi-table-th-${i}`} column={column} totalSize={totalSize} />
        ))}
        {withSubRow && <th />}
      </tr>
    </thead>
  );
}

interface RpiTableThProps<T> {
  column: RpiTableColumn<T>;
  totalSize: number;
}

export function RpiTableTh<T>({ column, totalSize }: RpiTableThProps<T>): React.ReactElement {
  const withSortBy = useRpiTableSortBy();
  const [width, setWidth] = React.useState<number>(() =>
    isNumber(column.minSize) && isNumber(column.maxSize) && column.minSize === column.maxSize
      ? column.minSize!
      : column.size || 100
  );

  const [isResizing, setIsResizing] = React.useState(false);

  const sortByKey = column.sortKey;
  const isSortable = !!(withSortBy && sortByKey);

  const content = React.useMemo(() => {
    const headerRender = (
      <div
        className="ellipsis"
        children={typeof column.header === "function" ? React.createElement(column.header) : column.header}
      />
    );

    if (!isSortable && !column.tooltip && (!column.align || column.align === "left")) return headerRender;

    let sortByRender;
    if (isSortable) {
      const isActivelySorted = withSortBy?.sortBy?.by === sortByKey;
      const iconName = !isActivelySorted
        ? "unfold_more"
        : withSortBy!.sortBy!.sortDirection === GraphQL.SortDirection.Asc
        ? "arrow_upward"
        : "arrow_downward";
      sortByRender = <IconV3 icon={iconName} size={16} style={{ flexShrink: 0, marginLeft: "auto" }} />;
    }

    return (
      <Flex.Row
        justify={column.align ? justifyFromAlign(column.align) : "flex-start"}
        align="center"
        sx={{ minWidth: 0 }}
        gap={4}
      >
        {headerRender}
        {column.tooltip && (
          <Tooltip label={column.tooltip} withArrow multiline width={column.tooltip.length > 25 ? 256 : "auto"}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <InfoCircle size={16} />
            </Box>
          </Tooltip>
        )}
        {sortByRender}
      </Flex.Row>
    );
  }, [column, withSortBy, isSortable]);

  const resizer = React.useMemo(() => {
    if (isNumber(column.minSize) && isNumber(column.maxSize) && column.minSize === column.maxSize) return;

    return (
      <Resizer
        width={width}
        onChange={(newWidth) => {
          // TODO: Table default configuration
          const minSize = column.minSize || 100;
          const maxSize = column.maxSize;

          if (newWidth < minSize) newWidth = minSize;
          else if (maxSize !== undefined && newWidth > maxSize) newWidth = maxSize;

          setWidth(newWidth);
        }}
        setIsResizing={(isResizing) => {
          if (isResizing) return setIsResizing(isResizing);
          setTimeout(() => setIsResizing(isResizing), 0);
        }}
      />
    );
  }, [column, width]);

  const handleSortBy = React.useCallback(() => {
    if (!withSortBy || !isSortable) return;

    const isCurrentColumn = withSortBy.sortBy?.by === sortByKey;
    const isCurrentlyAsc = withSortBy.sortBy?.sortDirection === GraphQL.SortDirection.Asc;
    const nextDirection = isCurrentColumn && !isCurrentlyAsc ? GraphQL.SortDirection.Asc : GraphQL.SortDirection.Desc;

    withSortBy.setSortBy(() => ({ by: sortByKey, sortDirection: nextDirection }));
  }, [withSortBy, isSortable, sortByKey]);

  const isActivelySorted = isSortable && withSortBy?.sortBy?.by === sortByKey;
  const isAsc = withSortBy?.sortBy?.sortDirection === GraphQL.SortDirection.Asc;
  const columnLabelForA11y = typeof column.header === "string" ? column.header : sortByKey ?? "column";

  const ariaSort: React.AriaAttributes["aria-sort"] = !isSortable
    ? undefined
    : !isActivelySorted
    ? "none"
    : isAsc
    ? "ascending"
    : "descending";

  const sortActionLabel = !isSortable
    ? undefined
    : !isActivelySorted
    ? `Sort by ${columnLabelForA11y}`
    : `Sorted by ${columnLabelForA11y} ${isAsc ? "ascending" : "descending"}. Activate to sort ${
        isAsc ? "descending" : "ascending"
      }.`;

  const widthPercent = `${(width / totalSize) * 100}%`;

  return (
    <th
      aria-sort={ariaSort}
      aria-label={isSortable ? columnLabelForA11y : undefined}
      style={{
        width: widthPercent,
        ...(column.align && { textAlign: column.align }),
        ...(column.noPadding && { padding: "0px" }),
      }}
    >
      {isSortable ? (
        <button
          type="button"
          aria-label={sortActionLabel}
          onClick={!isResizing ? handleSortBy : undefined}
          style={sortButtonStyle}
        >
          {content}
        </button>
      ) : (
        content
      )}
      {resizer}
    </th>
  );
}

const sortButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: 0,
  padding: 0,
  margin: 0,
  font: "inherit",
  color: "inherit",
  display: "block",
  width: "100%",
  textAlign: "inherit",
  cursor: "pointer",
};

interface ResizerProps {
  width: number;
  onChange: (width: number) => void;
  setIsResizing: (isResizing: boolean) => void;
}

const Resizer: React.FC<ResizerProps> = ({ width, onChange, setIsResizing }) => {
  const [isListening, setIsListening] = React.useState<boolean>(false);
  const [position, setPosition] = React.useState<{ x: number; width: number } | null>(null);

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (!position) return;
      const newWidth = position.width + (e.pageX - position.x);
      onChange(newWidth < 0 ? 0 : newWidth);
    },
    [position, onChange]
  );

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const position = {
        x: e.pageX,
        width,
      };

      setPosition(position);
      setIsResizing(true);
    },
    [width, setIsResizing]
  );

  const handleMouseUp = React.useCallback(() => {
    document.removeEventListener("mouseup", handleMouseUp);
    document.removeEventListener("mousemove", handleMouseMove);

    setPosition(null);
    setIsListening(false);
    setIsResizing(false);
  }, [handleMouseMove, setIsResizing]);

  React.useEffect(() => {
    if (position) {
      if (!isListening) {
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("mousemove", handleMouseMove);
        setIsListening(true);
      }
    }
  }, [isListening, position, handleMouseMove, handleMouseUp]);

  return <div className="resizer" onMouseDown={handleMouseDown} onClick={(e) => e.stopPropagation()} />;
};
